import React, { useState, useEffect } from 'react';
import { luxuryHireApi, LuxuryVehicle, VehicleListResponse } from '../../../services/luxuryHireApi';

const CATEGORY_OPTIONS = [
  { value: 'luxury_sedan', label: 'Luxury Sedan' },
  { value: 'sports_car', label: 'Sports Car' },
  { value: 'suv', label: 'Luxury SUV' },
  { value: 'ultra_luxury', label: 'Ultra Luxury' },
  { value: 'bus', label: 'Luxury Bus' },
  { value: 'van', label: 'Executive Van' },
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available', color: 'bg-green-500' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-yellow-500' },
  { value: 'inactive', label: 'Inactive', color: 'bg-slate-500' },
];

const formatCurrency = (amount: number, currency: string = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

interface VehicleFormData {
  name: string;
  brand: string;
  model: string;
  year: number | undefined;
  color: string;
  vehicle_category: string;
  passenger_capacity: number;
  description: string;
  hourly_rate: number;
  half_day_rate: number | undefined;
  daily_rate: number;
  included_km: number;
  extra_km_rate: number;
  security_required: boolean;
  security_fee: number;
  minimum_hours: number;
  deposit_percentage: number;
  cancellation_hours: number;
  status: string;
  is_featured: boolean;
  features: string[];
}

const initialFormData: VehicleFormData = {
  name: '',
  brand: '',
  model: '',
  year: undefined,
  color: '',
  vehicle_category: 'luxury_sedan',
  passenger_capacity: 4,
  description: '',
  hourly_rate: 50000,
  half_day_rate: undefined,
  daily_rate: 300000,
  included_km: 100,
  extra_km_rate: 500,
  security_required: false,
  security_fee: 50000,
  minimum_hours: 2,
  deposit_percentage: 30,
  cancellation_hours: 48,
  status: 'available',
  is_featured: false,
  features: [],
};

const VehicleManagement: React.FC = () => {
  const [vehicles, setVehicles] = useState<LuxuryVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<LuxuryVehicle | null>(null);
  const [formData, setFormData] = useState<VehicleFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [featureInput, setFeatureInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchVehicles();
  }, [page, statusFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params: any = { page, per_page: 10 };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const response: VehicleListResponse = await luxuryHireApi.getVehicles(params);
      setVehicles(response.vehicles);
      setTotalPages(response.total_pages);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingVehicle(null);
    setFormData(initialFormData);
    setShowModal(true);
    setError(null);
  };

  const openEditModal = (vehicle: LuxuryVehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name,
      brand: vehicle.brand,
      model: vehicle.model || '',
      year: vehicle.year,
      color: vehicle.color || '',
      vehicle_category: vehicle.vehicle_category,
      passenger_capacity: vehicle.passenger_capacity,
      description: vehicle.description || '',
      hourly_rate: vehicle.hourly_rate,
      half_day_rate: vehicle.half_day_rate,
      daily_rate: vehicle.daily_rate,
      included_km: vehicle.included_km,
      extra_km_rate: vehicle.extra_km_rate,
      security_required: vehicle.security_required,
      security_fee: vehicle.security_fee,
      minimum_hours: vehicle.minimum_hours,
      deposit_percentage: vehicle.deposit_percentage,
      cancellation_hours: vehicle.cancellation_hours,
      status: vehicle.status,
      is_featured: vehicle.is_featured,
      features: vehicle.features || [],
    });
    setShowModal(true);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const dataToSubmit = {
        ...formData,
        year: formData.year || undefined,
        half_day_rate: formData.half_day_rate || undefined,
      };

      if (editingVehicle) {
        await luxuryHireApi.updateVehicle(editingVehicle.id, dataToSubmit);
      } else {
        await luxuryHireApi.createVehicle(dataToSubmit);
      }

      setShowModal(false);
      fetchVehicles();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicle: LuxuryVehicle) => {
    if (!window.confirm(`Are you sure you want to delete "${vehicle.name}"?`)) {
      return;
    }

    try {
      await luxuryHireApi.deleteVehicle(vehicle.id);
      fetchVehicles();
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const addFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature),
    }));
  };

  return (
    <div className="p-6 bg-slate-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Luxury Vehicle Management</h1>
            <p className="text-slate-400 mt-1">Manage your fleet of luxury vehicles for hire</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Vehicle
          </button>
        </div>

        <div className="flex gap-4 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-amber-500/30 rounded-full animate-spin border-t-amber-500"></div>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
            <div className="text-6xl mb-4">🚗</div>
            <p className="text-slate-400">No vehicles found. Add your first luxury vehicle!</p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="lg:hidden space-y-4">
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-16 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                      {vehicle.thumbnail_url ? (
                        <img src={vehicle.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🚗</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{vehicle.name}</p>
                      <p className="text-slate-400 text-sm">{vehicle.brand} {vehicle.year}</p>
                      <p className="text-amber-400 font-medium mt-1">{formatCurrency(vehicle.daily_rate, vehicle.currency)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        vehicle.status === 'available' ? 'bg-green-500/20 text-green-400' :
                        vehicle.status === 'maintenance' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {STATUS_OPTIONS.find(s => s.value === vehicle.status)?.label || vehicle.status}
                      </span>
                      {vehicle.is_featured && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => openEditModal(vehicle)}
                        className="px-3 py-1.5 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(vehicle)}
                        className="px-3 py-1.5 bg-red-500/20 text-red-400 text-sm rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden lg:block bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Vehicle</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Daily Rate</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Status</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                            {vehicle.thumbnail_url ? (
                              <img src={vehicle.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-2xl">🚗</div>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{vehicle.name}</p>
                            <p className="text-slate-400 text-sm">{vehicle.brand} {vehicle.year}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {CATEGORY_OPTIONS.find(c => c.value === vehicle.vehicle_category)?.label || vehicle.vehicle_category}
                      </td>
                      <td className="px-6 py-4 text-amber-400 font-medium">
                        {formatCurrency(vehicle.daily_rate, vehicle.currency)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          vehicle.status === 'available' ? 'bg-green-500/20 text-green-400' :
                          vehicle.status === 'maintenance' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {STATUS_OPTIONS.find(s => s.value === vehicle.status)?.label || vehicle.status}
                        </span>
                        {vehicle.is_featured && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
                            Featured
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => openEditModal(vehicle)}
                          className="px-3 py-1.5 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-colors mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(vehicle)}
                          className="px-3 py-1.5 bg-red-500/20 text-red-400 text-sm rounded-lg hover:bg-red-500/30 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-slate-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-12">
              <div className="fixed inset-0 bg-black/70" onClick={() => setShowModal(false)} />
              
              <div className="relative bg-slate-800 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-slate-800 px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">
                    {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {error && (
                    <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Vehicle Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        placeholder="e.g., Rolls Royce Phantom 2024"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Brand *</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                        required
                        placeholder="e.g., Rolls Royce"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Model</label>
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                        placeholder="e.g., Phantom"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Year</label>
                      <input
                        type="number"
                        value={formData.year || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value ? parseInt(e.target.value) : undefined }))}
                        placeholder="e.g., 2024"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Color</label>
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        placeholder="e.g., Black"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Category *</label>
                      <select
                        value={formData.vehicle_category}
                        onChange={(e) => setFormData(prev => ({ ...prev, vehicle_category: e.target.value }))}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      >
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Passenger Capacity *</label>
                      <input
                        type="number"
                        value={formData.passenger_capacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, passenger_capacity: parseInt(e.target.value) }))}
                        min={1}
                        required
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>

                  <div className="border-t border-slate-700 pt-6">
                    <h3 className="text-lg font-medium text-white mb-4">Pricing</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Hourly Rate (₦) *</label>
                        <input
                          type="number"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) }))}
                          min={0}
                          required
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Half-Day Rate (₦)</label>
                        <input
                          type="number"
                          value={formData.half_day_rate || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, half_day_rate: e.target.value ? parseFloat(e.target.value) : undefined }))}
                          min={0}
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Daily Rate (₦) *</label>
                        <input
                          type="number"
                          value={formData.daily_rate}
                          onChange={(e) => setFormData(prev => ({ ...prev, daily_rate: parseFloat(e.target.value) }))}
                          min={0}
                          required
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Included KM</label>
                      <input
                        type="number"
                        value={formData.included_km}
                        onChange={(e) => setFormData(prev => ({ ...prev, included_km: parseInt(e.target.value) }))}
                        min={0}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Extra KM Rate (₦)</label>
                      <input
                        type="number"
                        value={formData.extra_km_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, extra_km_rate: parseFloat(e.target.value) }))}
                        min={0}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Minimum Hours</label>
                      <input
                        type="number"
                        value={formData.minimum_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, minimum_hours: parseInt(e.target.value) }))}
                        min={1}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Deposit %</label>
                      <input
                        type="number"
                        value={formData.deposit_percentage}
                        onChange={(e) => setFormData(prev => ({ ...prev, deposit_percentage: parseFloat(e.target.value) }))}
                        min={0}
                        max={100}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Cancellation Hours</label>
                      <input
                        type="number"
                        value={formData.cancellation_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, cancellation_hours: parseInt(e.target.value) }))}
                        min={0}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-700 pt-6">
                    <h3 className="text-lg font-medium text-white mb-4">Security</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.security_required}
                            onChange={(e) => setFormData(prev => ({ ...prev, security_required: e.target.checked }))}
                            className="sr-only"
                          />
                          <div className={`w-10 h-5 rounded-full transition-colors ${formData.security_required ? 'bg-amber-500' : 'bg-slate-600'}`}>
                            <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${formData.security_required ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
                          </div>
                          <span className="ml-3 text-slate-300">Security Required</span>
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Security Fee (₦/day)</label>
                        <input
                          type="number"
                          value={formData.security_fee}
                          onChange={(e) => setFormData(prev => ({ ...prev, security_fee: parseFloat(e.target.value) }))}
                          min={0}
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                          className="sr-only"
                        />
                        <div className={`w-10 h-5 rounded-full transition-colors ${formData.is_featured ? 'bg-amber-500' : 'bg-slate-600'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${formData.is_featured ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
                        </div>
                        <span className="ml-3 text-slate-300">Featured Vehicle</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Features</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={featureInput}
                        onChange={(e) => setFeatureInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                        placeholder="Add a feature..."
                        className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.features.map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full bg-slate-700 text-slate-300 text-sm"
                        >
                          {feature}
                          <button
                            type="button"
                            onClick={() => removeFeature(feature)}
                            className="ml-2 text-slate-400 hover:text-white"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : editingVehicle ? 'Update Vehicle' : 'Create Vehicle'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleManagement;
